import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Library, Save, Music4, Lock, Crown, ChevronDown, FolderOpen, Wand2, ChevronsUpDown, ChevronsDownUp, Radio, ListMusic, ArrowLeft, Plus, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import { api, apiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RepertoireEditor from "@/components/RepertoireEditor";
import FileViewer from "@/components/FileViewer";
import RepertoireSongRow from "@/components/RepertoireSongRow";
import StageLite from "@/components/StageLite";
import { SongRowAll } from "@/pages/Library";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
const UNCAT = "Sem categoria";

function transposeKey(key, steps) {
  if (!key) return key;
  const m = key.trim().match(/^([A-G])(#|b)?(.*)$/);
  if (!m) return key;
  let name = m[1] + (m[2] || "");
  if (FLAT[name]) name = FLAT[name];
  const idx = NOTES.indexOf(name);
  if (idx < 0) return key;
  return NOTES[(idx + steps + 12) % 12] + (m[3] || "");
}

export default function MyRepertoire() {
  const navigate = useNavigate();
  const [songs, setSongs] = useState([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [viewer, setViewer] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openCats, setOpenCats] = useState([]);
  const [plan, setPlan] = useState(null);
  const [structViewer, setStructViewer] = useState(null);
  const [stageOpen, setStageOpen] = useState(false);
  const [view, setView] = useState("hub");
  const [favOnly, setFavOnly] = useState(false);
  const [groups, setGroups] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = () => api.get("/songs").then((r) => setSongs(r.data)).catch(() => {});
  const loadGroups = () => api.get("/repertoires").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/me/plan").then((r) => {
      setPlan(r.data);
      if (r.data?.repertoire_access?.allowed !== false) { load(); loadGroups(); }
    }).catch(() => setPlan({ repertoire_access: { allowed: true }, repertoire_limits: {} }));
  }, []);

  const access = plan?.repertoire_access;
  const limits = plan?.repertoire_limits || {};
  const totalLim = limits.rep_songs_total;
  const atTotal = totalLim >= 0 && songs.length >= totalLim;

  const saveDraft = async () => {
    const valid = draft.filter((s) => s.title?.trim() && !s.id && !s.file_id).map((s) => ({ ...s, category: newCat.trim() }));
    if (valid.length === 0) return toast.error("Adicione ao menos uma música (sem arquivo já salvo).");
    try { const { data } = await api.post("/songs/bulk", { songs: valid }); toast.success(`${data.created.length} música(s) salva(s) na biblioteca!`); setDraft([]); setNewCat(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const updSaved = (id, k, v) => setSongs((prev) => prev.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  const persist = async (s, patch) => {
    try {
      await api.put(`/songs/${s.id}`, { title: s.title, artist: s.artist || "", key: s.key || "", lyrics: s.lyrics || "", chords: s.chords || "", notes: s.notes || "", category: s.category || "", tags: s.tags || [], ...patch });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveOne = async (s) => { await persist(s, {}); toast.success("Atualizada!"); };
  const transpose = async (s, delta) => {
    const nk = transposeKey(s.key, delta);
    updSaved(s.id, "key", nk);
    await persist({ ...s, key: nk }, { key: nk });
  };
  const delOne = async (id) => { try { await api.delete(`/songs/${id}`); toast.success("Removida."); if (openId === id) setOpenId(null); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleFav = async (s) => {
    const next = !s.favorite;
    updSaved(s.id, "favorite", next);
    try { await api.patch(`/songs/${s.id}/favorite`, { favorite: next }); toast.success(next ? "Adicionada à biblioteca!" : "Removida da biblioteca."); }
    catch (e) { updSaved(s.id, "favorite", !next); toast.error(apiError(e.response?.data?.detail)); }
  };

  const persistOrder = (flat) => { api.put("/songs/order", { ids: flat.map((s) => s.id) }).catch(() => {}); };
  const onDragFlat = ({ active, over }) => {
    if (!over || active.id === over.id || q || favOnly) return;
    const oldI = songs.findIndex((x) => x.id === active.id);
    const newI = songs.findIndex((x) => x.id === over.id);
    if (oldI < 0 || newI < 0) return;
    const next = arrayMove(songs, oldI, newI); setSongs(next); persistOrder(next);
  };

  const setlistSongIds = useMemo(() => { const set = new Set(); (groups || []).forEach((g) => (g.songs || []).forEach((x) => { if (x.song_id) set.add(x.song_id); })); return set; }, [groups]);
  const filtered = useMemo(() => songs.filter((s) => (!favOnly || s.favorite) && (!q || s.title.toLowerCase().includes(q.toLowerCase()) || (s.artist || "").toLowerCase().includes(q.toLowerCase()))), [songs, q, favOnly]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => { const c = (s.category || "").trim() || UNCAT; (map[c] = map[c] || []).push(s); });
    return Object.entries(map).sort((a, b) => (a[0] === UNCAT ? 1 : b[0] === UNCAT ? -1 : a[0].localeCompare(b[0])));
  }, [filtered]);

  // reordena uma seção e persiste a nova ordem global (concatenação das seções em ordem de exibição)
  const commit = (newGrouped) => {
    const flat = newGrouped.flatMap(([, list]) => list);
    setSongs(flat);
    persistOrder(flat);
  };
  const onDragEnd = (cat, catList) => ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = catList.findIndex((x) => x.id === active.id);
    const newI = catList.findIndex((x) => x.id === over.id);
    if (oldI < 0 || newI < 0) return;
    const newList = arrayMove(catList, oldI, newI);
    commit(grouped.map(([c, l]) => (c === cat ? [c, newList] : [c, l])));
  };
  const move = (cat, catList, id, dir) => {
    const i = catList.findIndex((x) => x.id === id); const j = i + dir;
    if (j < 0 || j >= catList.length) return;
    commit(grouped.map(([c, l]) => (c === cat ? [c, arrayMove(catList, i, j)] : [c, l])));
  };

  useEffect(() => { if (q) setOpenCats(grouped.map((g) => g[0])); }, [q, grouped]);

  if (!plan) return <div className="text-muted-foreground" data-testid="rep-loading">Carregando...</div>;

  if (plan && access && access.allowed === false) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="repertoire-locked">
        <div className="bg-surface border border-border rounded-lg p-8 text-center flex flex-col items-center gap-4">
          <div className="h-14 w-14 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center"><Lock className="h-7 w-7 text-primary" /></div>
          <h1 className="font-head text-2xl font-bold">Repertório indisponível no momento</h1>
          <p className="text-muted-foreground text-sm">No Plano Gratuito, o Repertório fica acessível apenas enquanto você tiver um evento em aberto. Seus setlists e músicas <b>não foram apagados</b> — voltam a aparecer quando você abrir um evento, comprar um Evento Avulso ou assinar um plano.</p>
          <div className="flex gap-2">
            <Button data-testid="rep-locked-plans" onClick={() => navigate("/meu-plano")} className="rounded-md gap-2"><Crown className="h-4 w-4" />Ver planos</Button>
            <Button variant="outline" onClick={() => navigate("/eventos/novo")} className="rounded-md bg-transparent">Criar evento</Button>
          </div>
        </div>
      </div>
    );
  }

  const inp = "bg-background border-border rounded-md";

  const HUB_ACTIONS = [
    { key: "biblioteca", label: "Biblioteca", desc: "Suas músicas e setlists", Icon: Library, onClick: () => navigate("/repertorio/biblioteca") },
    { key: "web", label: "Buscar na Web", desc: "Encontre e formate cifras", Icon: Wand2, onClick: () => navigate("/repertorio/formatar") },
    { key: "add", label: "Adicionar / Importar", desc: "Cadastre ou importe músicas", Icon: Plus, onClick: () => setView("add") },
    { key: "setlist", label: "Setlist", desc: "Monte e reutilize setlists", Icon: ListMusic, onClick: () => navigate("/repertorio/biblioteca?tab=setlists") },
    { key: "stage", label: "Modo Palco", desc: "Ensaie em tela cheia", Icon: Radio, onClick: () => (songs.length ? setStageOpen(true) : toast.error("Adicione músicas antes de usar o Modo Palco.")) },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="my-repertoire-page">
      <section className="relative overflow-hidden rounded-2xl border border-accent/20 bg-gradient-to-br from-accent/10 via-card to-card p-5 sm:p-7">
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/15 text-accent shrink-0"><Library className="h-6 w-6" /></span>
            <div className="min-w-0">
              <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Meu <span className="bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent">Repertório</span></h1>
              <p className="text-sm text-muted-foreground mt-0.5">Sua biblioteca pessoal — arraste para reordenar; a ordem é salva automaticamente.</p>
            </div>
          </div>
          {access?.allowed !== false && view !== "hub" && (
            <div className="mt-4">
              <Button data-testid="rep-back-hub" variant="outline" onClick={() => setView("hub")} className="rounded-xl gap-2 bg-transparent border-white/15"><ArrowLeft className="h-4 w-4" />Voltar</Button>
            </div>
          )}
          {plan && (
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]" data-testid="rep-limits">
              <span className={`px-2.5 py-1 rounded-full border ${atTotal ? "border-destructive/50 text-destructive bg-destructive/10" : "border-border text-muted-foreground bg-background/40"}`}>Músicas: {songs.length}{totalLim >= 0 ? ` / ${totalLim}` : " · ilimitado"}</span>
              <span className="px-2.5 py-1 rounded-full border border-border text-muted-foreground bg-background/40">Setlists: {limits.rep_groups_limit >= 0 ? `até ${limits.rep_groups_limit}` : "ilimitado"}</span>
              {access?.temporary && <span className="px-2.5 py-1 rounded-full border border-warning/50 text-warning bg-warning/10">Acesso temporário (evento aberto)</span>}
            </div>
          )}
        </div>
      </section>

      {access?.allowed !== false && view === "hub" && (
        <section data-testid="rep-hub" className="grid grid-cols-2 gap-3 sm:gap-4">
          {HUB_ACTIONS.map(({ key, label, desc, Icon, onClick }) => (
            <button key={key} data-testid={`rep-hub-${key}`} onClick={onClick}
              className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-surface p-5 text-left transition-all hover:border-accent/50 hover:bg-accent/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent min-h-[124px]">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/15 text-accent transition-transform group-hover:scale-105"><Icon className="h-6 w-6" /></span>
              <div><div className="font-head font-semibold text-base">{label}</div><div className="text-xs text-muted-foreground mt-0.5">{desc}</div></div>
            </button>
          ))}
        </section>
      )}

      {view === "add" && (
      <div className="bg-surface border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-head text-xl font-semibold">Adicionar / Importar</h2>
        {atTotal && <p className="text-xs text-destructive flex items-center gap-1.5" data-testid="rep-total-warning"><Lock className="h-3.5 w-3.5" />Você atingiu o limite de {totalLim} músicas disponível no seu plano.</p>}
        <RepertoireEditor songs={draft} onChange={setDraft} locked={atTotal} limit={totalLim} used={songs.length} library onManual={() => navigate("/repertorio/nova")} onSaved={load} />
      </div>
      )}

      {view === "add" && (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-head text-xl font-semibold">Minhas músicas ({songs.length})</h2>
          <div className="flex items-center gap-2">
            <Button data-testid="rep-fav-filter" variant="outline" size="sm" onClick={() => setFavOnly((v) => !v)} className={`rounded-full gap-1.5 text-xs shrink-0 ${favOnly ? "border-primary/50 text-primary bg-primary/10" : "bg-transparent"}`}>
              <Star className={`h-3.5 w-3.5 ${favOnly ? "fill-current" : ""}`} />Biblioteca
            </Button>
            {grouped.length > 0 && false && (
              <Button data-testid="rep-toggle-all" variant="outline" size="sm" onClick={() => setOpenCats(openCats.length === grouped.length ? [] : grouped.map((g) => g[0]))} className="rounded-full bg-transparent gap-1.5 text-xs shrink-0">
                {openCats.length === grouped.length ? <><ChevronsDownUp className="h-3.5 w-3.5" />Recolher todas</> : <><ChevronsUpDown className="h-3.5 w-3.5" />Ver todas</>}
              </Button>
            )}
            <Input data-testid="my-rep-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título ou artista" className={`${inp} max-w-[220px]`} />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-lg p-12 text-center flex flex-col items-center gap-2"><Music4 className="h-8 w-8 opacity-60" />{q ? "Nenhuma música corresponde à busca." : "Nenhuma música na sua biblioteca ainda."}</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragFlat}>
            <SortableContext items={filtered.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" data-testid="rep-song-list">
                {filtered.map((s, i) => (
                  <SongRowAll key={s.id} s={s} index={i} draggable={!q && !favOnly} inSetlist={setlistSongIds.has(s.id)}
                    onOpen={() => navigate(`/repertorio/musica/${s.id}`)}
                    onTranspose={(d) => transpose(s, d)} onDelete={() => delOne(s.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      )}
      <FileViewer open={!!viewer} onClose={() => setViewer(null)} fileId={viewer?.file_id} fileKind={viewer?.file_kind} title={viewer?.title} />
      <Dialog open={!!structViewer} onOpenChange={(o) => !o && setStructViewer(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="struct-viewer-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-accent" />Formatação Mí Chamou</DialogTitle></DialogHeader>
          {structViewer?.structured && <ChordSheetViewer structured={structViewer.structured} music={structViewer.title} artist={structViewer.artist} />}
        </DialogContent>
      </Dialog>
      <StageLite open={stageOpen} songs={songs} onClose={() => setStageOpen(false)} />
    </div>
  );
}
